import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import _keyBy from 'lodash/keyBy';
import {
  DocumentationCategory,
  FilterGroupingEnum,
  useEdmDocumentationCategoriesQuery,
  useEdmDocumentationsAnalyticsByCategoryQuery,
} from 'graphql-common';
import {
  GetUrlParams,
  ORDERING_DIRECTION_ASC,
  ORDERING_DIRECTION_DESC,
  OrderingParams,
  PER_PAGE_KEY_DEFAULT,
} from '@lib/enums/urls';
import WhiteBox from '@lib/components/WhiteBox/WhiteBox';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import PageHeader from '@lib/layouts/DashboardLayout/PageHeader/PageHeader';
import EmptyState from '@lib/components/EmptyState/EmptyState';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import { IconName } from '@lib/components/Modal/enums';
import getPeriodInput from '../utils/getPeriodInput';
import getCategorySorts from './utils/getCategorySorts';
import { CategoryAnalyticsRow } from './utils/getCategoryTableColumns';
import CategoriesTable from './CategoriesTable/CategoriesTable';
import CategoriesPie from './CategoriesPie/CategoriesPie';
import styles from './TopCategoriesByViews.module.scss';

interface Props {
  period: string[];
  isDefaultPeriod: boolean;
  onClearPeriod: () => void;
  // Reports the first successful data load up to the page-level loader.
  onFirstLoaded?: () => void;
}

const defaultOrderingParams: OrderingParams = {
  [GetUrlParams.OrderingField]: 'viewCount',
  [GetUrlParams.OrderingDirection]: ORDERING_DIRECTION_DESC,
};

export default function TopCategoriesByViews({
  period,
  isDefaultPeriod,
  onClearPeriod,
  onFirstLoaded,
}: Props) {
  const { t } = useTranslation();
  const orderingState = useState<OrderingParams>(defaultOrderingParams);
  const [orderingParams] = orderingState;

  const periodInput = getPeriodInput(period);
  const sorts = getCategorySorts(
    orderingParams[GetUrlParams.OrderingField],
    orderingParams[GetUrlParams.OrderingDirection],
  );

  const analyticsQuery = useEdmDocumentationsAnalyticsByCategoryQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      period: periodInput || { startDate: '', endDate: '' },
      sorts,
      limit: +PER_PAGE_KEY_DEFAULT,
    },
    skip: !periodInput,
  });

  const analyticsRows = _get(analyticsQuery, ['data', 'data'], []) as Array<{
    categoryId: string;
    viewCount: number;
    percentage: number;
  }>;

  const categoryIds = analyticsRows.map((r) => r.categoryId);

  const categoriesQuery = useEdmDocumentationCategoriesQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      page: 1,
      limit: categoryIds.length || 1,
      filters: categoryIds.length
        ? [
            {
              id: {
                grouping: FilterGroupingEnum.And,
                predicate: { in: categoryIds },
              },
            },
          ]
        : undefined,
    },
    skip: !categoryIds.length,
  });

  const categoriesById = useMemo(() => {
    const collection = _get(
      categoriesQuery,
      ['data', 'data', 'collection'],
      [],
    ) as DocumentationCategory[];
    return _keyBy(collection, 'id');
  }, [categoriesQuery]);

  const rows: CategoryAnalyticsRow[] = useMemo(() => {
    const orderingField = orderingParams[GetUrlParams.OrderingField];
    const orderingDirection = orderingParams[GetUrlParams.OrderingDirection];
    const built = analyticsRows.map((row) => {
      const cat = categoriesById[row.categoryId];
      return {
        id: row.categoryId,
        categoryId: row.categoryId,
        name: cat?.name || '',
        color: cat?.color || null,
        viewCount: row.viewCount,
        percentage: row.percentage,
      };
    });
    if (orderingField === 'categoryName') {
      return [...built].sort((a, b) => {
        const cmp = a.name.localeCompare(b.name);
        return orderingDirection === ORDERING_DIRECTION_ASC ? cmp : -cmp;
      });
    }
    return built;
  }, [analyticsRows, categoriesById, orderingParams]);

  const isLoading = analyticsQuery.loading || categoriesQuery.loading;
  const hasNoActivity = !isLoading && rows.length === 0;

  // Fire `onFirstLoaded` once both the analytics-by-category and category
  // details (name/color) queries have settled.
  const firstLoadReportedRef = useRef(false);
  useEffect(() => {
    if (!isLoading && !firstLoadReportedRef.current) {
      firstLoadReportedRef.current = true;
      onFirstLoaded?.();
    }
  }, [isLoading, onFirstLoaded]);

  return (
    <>
      <PageHeader title={t('top-document-categories-by-views')} />
      <PageBody>
        {hasNoActivity ? (
          <WhiteBox verticalPaddings="small" horizontalPaddings="small">
            <EmptyState
              icon={IconName.SearchOff}
              title={
                isDefaultPeriod ? t('no-activity-yet') : t('no-results-found')
              }
              text={
                isDefaultPeriod
                  ? t('no-activity-yet-text')
                  : t('no-results-found-text')
              }
              actions={
                isDefaultPeriod
                  ? undefined
                  : [
                      <Button
                        key="clear"
                        buttonText={t('clear-filter')}
                        buttonType={ButtonTypes.primaryFilled}
                        onClick={onClearPeriod}
                      />,
                    ]
              }
            />
          </WhiteBox>
        ) : (
          <div className={styles.row}>
            <div>
              <WhiteBox verticalPaddings="small" horizontalPaddings="small">
                <CategoriesTable
                  rows={rows}
                  loading={isLoading}
                  orderingState={orderingState}
                />
              </WhiteBox>
            </div>
            <div>
              <WhiteBox verticalPaddings="small" horizontalPaddings="small">
                <CategoriesPie rows={rows} />
              </WhiteBox>
            </div>
          </div>
        )}
      </PageBody>
    </>
  );
}
